// src/components/UserGuideOverlay.js
import React, { useState, useLayoutEffect, useRef } from 'react';
import useGuideScrollExtension from '../utils/useGuideScrollExtension';

// Below this viewport width the overlay is hidden and the toggle in the dashboard disappears.
const BREAKPOINT = 900;
const TIP = 15; // CSS triangle half-width / height (px)
const SPOT_PAD = 12; // padding around spotlight target (px)

const UserGuideOverlay = ({
    onClose,
    // --- dual-mode props (default to "show all" for backwards compat with DemoDashboard) ---
    mode          = 'manual', // 'auto' | 'manual'
    autoStep      = null,     // 1|2|3|4 — only used when mode === 'auto'
    hasLessonPlans = true,
    hasClasses     = true,
    hasTutorials   = true,
}) => {
    const [isSmallScreen, setIsSmallScreen] = useState(() => window.innerWidth < BREAKPOINT);
    const [positions, setPositions] = useState(null);
    const box1Ref = useRef(null);
    const box2Ref = useRef(null);
    const box3Ref = useRef(null);
    const box4Ref = useRef(null);

    useLayoutEffect(() => {
        const calculate = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            if (vw < BREAKPOINT) {
                setIsSmallScreen(true);
                setPositions(null);
                return;
            }
            setIsSmallScreen(false);

            // Which steps are visible for this render
            const active = mode === 'auto'
                ? new Set(autoStep === '34' ? [3, 4] : [autoStep])
                : new Set([
                    1,
                    ...(hasLessonPlans || hasClasses || hasTutorials ? [2] : []),
                    ...(hasClasses     || hasTutorials               ? [3] : []),
                    ...(hasTutorials                                  ? [4] : []),
                ]);

            const createCardEl  = document.querySelector('[data-guide="create-card"]');
            const createFormEl  = document.querySelector('[data-guide="create-form"]');
            const planNameEl    = document.querySelector('[data-guide="plan-name"]');
            const firstClassEl  = document.querySelector('[data-guide="first-class"]');
            const showTutEl     = document.querySelector('[data-guide="show-tutorials"]');

            if (!createCardEl || !box1Ref.current || !box2Ref.current || !box3Ref.current || !box4Ref.current) return;

            const cc = createCardEl.getBoundingClientRect();
            const cf = createFormEl?.getBoundingClientRect() ?? cc;
            const pn = planNameEl?.getBoundingClientRect()   ?? null;
            const fc = firstClassEl?.getBoundingClientRect() ?? null;
            const st = showTutEl?.getBoundingClientRect()    ?? null;

            // Box dimensions are stable even when a box is off-screen at -9999
            const b1 = box1Ref.current.getBoundingClientRect();
            const b2 = box2Ref.current.getBoundingClientRect();
            const b3 = box3Ref.current.getBoundingClientRect();
            const b4 = box4Ref.current.getBoundingClientRect();

            const pad = 10;
            const newPos = {};

            // ── Step 1: arrowDown — tip at top-centre of the create form ────────────
            if (active.has(1)) {
                const tipX      = cf.left + cf.width / 2;
                const tipY      = cf.top;
                const boxLeft   = Math.max(pad, Math.min(tipX - b1.width / 2, vw - b1.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b1.width - TIP);
                newPos.box1 = { bottom: vh - tipY + TIP, left: boxLeft, arrowLeft };
            }

            // ── Step 2: arrowDown — tip at centre of first lesson plan h3 ──────────
            if (active.has(2) && pn) {
                const tipX = pn.left + pn.width  / 2;
                const tipY = pn.top - 5;
                const boxLeft   = Math.max(pad, Math.min(tipX - b2.width / 2, vw - b2.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b2.width - TIP);
                newPos.box2 = { bottom: vh - tipY + TIP, left: boxLeft, arrowLeft };
            }

            // ── Step 3: arrowLeft — tip just right of first teal class link ─────────
            if (active.has(3) && fc) {
                const tipX    = fc.right + 5;
                const tipY    = fc.top   + fc.height / 2;
                const boxLeft = Math.min(tipX + TIP, vw - b3.width - pad);
                const boxTop  = Math.max(pad, Math.min(tipY - b3.height / 2, vh - b3.height - pad));
                newPos.box3 = { top: boxTop, left: boxLeft };
            }

            // ── Step 4: arrowRight — tip just left of "Show Tutorials" span ─────────
            if (active.has(4) && st) {
                const tipX    = st.left - 5;
                const tipY    = st.top  + st.height / 2;
                const boxLeft = Math.max(pad, tipX - TIP - b4.width);
                const boxTop  = Math.max(pad, Math.min(tipY - b4.height / 2, vh - b4.height - pad));
                newPos.box4 = { top: boxTop, left: boxLeft };
            }

            // ── Spotlight ──────────────────────────────────────────────────────────
            if (mode === 'auto') {
                if (autoStep === '34') {
                    const spots = [];
                    if (fc) spots.push({ x: fc.left, y: fc.top, w: fc.width, h: fc.height });
                    if (st) spots.push({ x: st.left, y: st.top, w: st.width, h: st.height });
                    if (spots.length) newPos.spotlights = spots;
                } else {
                    let targetRect = null;
                    if (autoStep === 1)      targetRect = cf;
                    else if (autoStep === 2) targetRect = pn;
                    else if (autoStep === 3) targetRect = fc;
                    else if (autoStep === 4) targetRect = st;
                    if (targetRect) {
                        newPos.spotlights = [{ x: targetRect.left, y: targetRect.top, w: targetRect.width, h: targetRect.height }];
                    }
                }
            }

            setPositions(newPos);
        };

        calculate();
        window.addEventListener('resize', calculate);
        window.addEventListener('scroll', calculate, { passive: true });
        return () => {
            window.removeEventListener('resize', calculate);
            window.removeEventListener('scroll', calculate);
        };
    }, [mode, autoStep, hasLessonPlans, hasClasses, hasTutorials]);

    useGuideScrollExtension(!isSmallScreen);

    if (isSmallScreen) return null;

    // ── Styles ───────────────────────────────────────────────────────────────────

    const overlayStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 9999,
        cursor: 'pointer',
        ...(mode === 'manual' ? { backgroundColor: 'rgba(45, 64, 66, 0.6)' } : {}),
        fontFamily: '"Comic Sans MS", "Chalkboard SE", "Marker Felt", sans-serif',
    };

    const boxBase = {
        position: 'fixed',
        backgroundColor: 'white',
        border: '3px solid #2D4042',
        borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px',
        padding: '20px',
        boxShadow: '8px 8px 0px rgba(0,0,0,0.3)',
        pointerEvents: mode === 'auto' ? 'auto' : 'none',
    };

    const markerStyle = {
        position: 'absolute', top: '-15px', left: '-15px',
        width: '35px', height: '35px', backgroundColor: '#75B9BE', color: 'white',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 'bold', fontSize: '1.2rem', border: '2px solid #2D4042',
        boxShadow: '3px 3px 0px rgba(0,0,0,0.2)',
    };

    const titleStyle = {
        margin: '0 0 5px 0', color: '#2D4042', fontSize: '1.1rem',
        fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px',
    };

    const textStyle = { margin: 0, color: '#555', fontSize: '0.95rem', lineHeight: '1.4' };

    // Boxes start off-screen (visibility: hidden) until positions are computed
    const getBoxStyle = (posData, maxWidth) => {
        if (!posData) return { ...boxBase, maxWidth, visibility: 'hidden', top: -9999, left: -9999 };
        const s = { ...boxBase, maxWidth, visibility: 'visible', left: posData.left };
        if (posData.bottom !== undefined) s.bottom = posData.bottom;
        if (posData.top    !== undefined) s.top    = posData.top;
        return s;
    };

    const makeArrowDown = (arrowLeft) => ({
        position: 'absolute', bottom: '-15px', left: arrowLeft ?? '50%',
        width: 0, height: 0,
        borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
        borderTop: '15px solid white', filter: 'drop-shadow(0px 3px 0px #2D4042)',
    });

    const arrowLeftStyle = {
        position: 'absolute', top: '50%', left: '-15px',
        width: 0, height: 0,
        borderTop: '10px solid transparent', borderBottom: '10px solid transparent',
        borderRight: '15px solid white', transform: 'translateY(-50%)',
        filter: 'drop-shadow(-3px 0px 0px #2D4042)',
    };

    const arrowRightStyle = {
        position: 'absolute', top: '50%', right: '-15px',
        width: 0, height: 0,
        borderTop: '10px solid transparent', borderBottom: '10px solid transparent',
        borderLeft: '15px solid white', transform: 'translateY(-50%)',
        filter: 'drop-shadow(3px 0px 0px #2D4042)',
    };

    const closeBtnStyle = {
        position: 'absolute', top: '6px', right: '8px',
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: '1.1rem', color: '#2D4042', fontWeight: 'bold', lineHeight: 1, padding: '2px 4px',
    };

    const spots = positions?.spotlights ?? [];

    return (
        <div style={overlayStyle} onClick={onClose}>

            {/* Spotlight SVG — only in auto mode */}
            {mode === 'auto' && (
                <svg style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                    <style>{`
                        @keyframes guide-pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.4; }
                        }
                        .guide-spotlight-ring { animation: guide-pulse 1.8s ease-in-out infinite; }
                    `}</style>
                    <defs>
                        <mask id="guide-spotlight-mask">
                            <rect width="100%" height="100%" fill="white" />
                            {spots.map((s, i) => (
                                <rect key={i}
                                    x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                                    width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                                    rx="8" fill="black"
                                />
                            ))}
                        </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(45,64,66,0.78)" mask="url(#guide-spotlight-mask)" />
                    {spots.map((s, i) => (
                        <rect key={i}
                            className="guide-spotlight-ring"
                            x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                            width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                            rx="8" fill="none" stroke="#75B9BE" strokeWidth="3"
                        />
                    ))}
                </svg>
            )}

            {/* Step 1: Create a Lesson Plan ✨ */}
            <div data-guide-popup="true" ref={box1Ref} style={getBoxStyle(positions?.box1, '280px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>✨</div>
                <p style={titleStyle}>1. Create a Lesson Plan</p>
                <p style={textStyle}>
                    Type in a topic or title, choose your number of lessons, and click <strong>Create</strong> to generate your new curriculum outline.
                </p>
                <div style={makeArrowDown(positions?.box1?.arrowLeft)} />
            </div>

            {/* Step 2: Upload Content 📂 */}
            <div data-guide-popup="true" ref={box2Ref} style={getBoxStyle(positions?.box2, '270px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>📂</div>
                <p style={titleStyle}>2. Upload Content</p>
                <p style={textStyle}>
                    Click on the name of any Lesson Plan or <strong>Manage Plan</strong> to open it up. That's where you'll upload your lesson content to generate custom tutorials!
                </p>
                <div style={makeArrowDown(positions?.box2?.arrowLeft)} />
            </div>

            {/* Step 3: Show Classes 👥 */}
            <div data-guide-popup="true" ref={box3Ref} style={getBoxStyle(positions?.box3, '250px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>👥</div>
                <p style={titleStyle}>3. Show Classes</p>
                <p style={textStyle}>
                    Each class you've added to a Lesson Plan appears here. Click it to manage students, generate tutorials, and track progress.
                </p>
                <div style={arrowLeftStyle} />
            </div>

            {/* Step 4: Show Tutorials 🎓 */}
            <div data-guide-popup="true" ref={box4Ref} style={getBoxStyle(positions?.box4, '250px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>🎓</div>
                <p style={titleStyle}>4. Show Tutorials</p>
                <p style={textStyle}>
                    Once generated, use this toggle to view the specific tutorials you've generated for each of your classes.
                </p>
                <div style={arrowRightStyle} />
            </div>

            <p style={{ position: 'fixed', bottom: '20px', width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '1.2rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
                Click anywhere to close
            </p>
        </div>
    );
};

export default UserGuideOverlay;
